import React, { useState, useEffect } from 'react';
import { faBuilding, faSave, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuth } from '../hooks/useAuth';
import { OrganizationService } from '../services/organizationService';
import FormInput from '../components/FormInput';
import Button from '../components/Button';
import Alert from '../components/Alert';
import PageHeader from '../components/PageHeader';
import LogoUpload from '../components/LogoUpload';
import { organizationSettingsSchema, OrganizationSettingsFormData } from '../schemas';

const OrganizationSettingsPage: React.FC = () => {
  const { currentOrganization, switchOrganization } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid, isDirty }
  } = useForm<OrganizationSettingsFormData>({
    resolver: zodResolver(organizationSettingsSchema),
    mode: 'onChange',
    defaultValues: {
      name: '',
      slogan: ''
    }
  });

  useEffect(() => {
    if (currentOrganization) {
      reset({
        name: currentOrganization.name || '',
        slogan: currentOrganization.slogan || ''
      });
    }
  }, [currentOrganization, reset]);

  const onSubmit = async (data: OrganizationSettingsFormData): Promise<void> => {
    if (!currentOrganization) {
      setError('Nenhuma organização selecionada');
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const result = await OrganizationService.updateOrganization(currentOrganization.id, {
        name: data.name,
        slogan: data.slogan
      });

      if (result.success) {
        setSuccess(result.message);

        // Update current organization in auth context
        if (result.data) {
          switchOrganization({
            ...currentOrganization,
            name: result.data.name,
            slogan: result.data.slogan,
            logo_url: result.data.logo_url
          });
        }
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('Erro interno do servidor');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogoChange = (logoUrl: string | null): void => {
    // Update current organization in context immediately for UI feedback
    if (currentOrganization) {
      switchOrganization({
        ...currentOrganization,
        logo_url: logoUrl || undefined
      });
    }
  };

  const handleBack = (): void => {
    navigate('/');
  };

  if (!currentOrganization) {
    return (
      <div className="w-full flex flex-col gap-6 flex-grow p-6">
        <Alert
          variant="error"
          message="Nenhuma organização encontrada. Você precisa estar associado a uma organização para acessar esta página."
        />
      </div>
    );
  }

  console.log({ currentOrganization });

  return (
    <div className="w-full flex flex-col gap-6 flex-grow p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <PageHeader
          title="Configurações da Organização"
          subtitle="Gerencie as informações básicas da sua organização"
        />
        <Button onClick={handleBack} variant="secondary" leftIcon={faArrowLeft}>
          Voltar
        </Button>
      </div>

      {/* Alerts */}
      {error && <Alert variant="error" message={error} onClose={() => setError('')} />}
      {success && <Alert variant="success" message={success} onClose={() => setSuccess('')} />}

      {/* Organization Settings Form */}
      <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
        <div className="space-y-8">
          {/* Logo Upload Section */}
          <div className="pb-6 border-b border-gray-200">
            <LogoUpload
              organizationId={currentOrganization.id}
              currentLogoUrl={currentOrganization.logo_url}
              onLogoChange={handleLogoChange}
              onError={setError}
              onSuccess={setSuccess}
            />
          </div>

          {/* Organization Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Organization Name */}
            <FormInput
              name="name"
              control={control}
              label="Nome da Organização"
              placeholder="Digite o nome da organização"
              required
              leftIcon={faBuilding}
            />

            {/* Organization Slogan */}
            <FormInput
              name="slogan"
              control={control}
              label="Slogan"
              placeholder="Digite o slogan da organização (opcional)"
            />

            {/* Form Actions */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <Button type="button" onClick={handleBack} variant="secondary">
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="primary"
                leftIcon={faSave}
                loading={isLoading}
                disabled={isLoading || !isValid || !isDirty}
              >
                {isLoading ? 'Salvando...' : 'Salvar Alterações'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default OrganizationSettingsPage;
